
import { TrendingUp, TrendingDown, LucideIcon } from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface StatCardProps {
  title: string;
  value?: string;
  change?: number;
  period?: string;
  chartData?: number[];
  isLoading?: boolean;
  icon?: LucideIcon;
}

const StatCard = ({ 
  title, 
  value = "0", 
  change, 
  period = "last month", 
  chartData, 
  isLoading = false,
  icon: Icon
}: StatCardProps) => {
  const isPositive = change && change >= 0;
  
  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardDescription className="text-xs uppercase text-vibebops-textMuted">{title}</CardDescription>
          <Skeleton className="h-8 w-20" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-4 w-32 mt-1" />
          <div className="h-16 mt-3 flex items-end gap-1">
            {Array(7).fill(0).map((_, idx) => (
              <Skeleton key={idx} className="flex-1 h-8" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardDescription className="text-xs uppercase text-vibebops-textMuted">{title}</CardDescription>
          <CardTitle className="text-3xl font-bold">{value}</CardTitle>
        </div>
        {Icon && (
          <div className="p-2 bg-vibebops-highlight rounded-full">
            <Icon className="h-5 w-5 text-vibebops-accent" />
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex items-center text-xs">
          {change !== undefined ? (
            <>
              {isPositive ? (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={isPositive ? 'text-green-500' : 'text-red-500'}>
                {isPositive ? '+' : ''}{change}% 
              </span>
              <span className="text-vibebops-textMuted ml-1">vs {period}</span>
            </>
          ) : (
            <span className="text-vibebops-textMuted">No change data available</span>
          )}
        </div>
        
        {chartData && chartData.length > 0 ? (
          <div className="h-16 mt-3 flex items-end gap-1">
            {chartData.map((value, idx) => (
              <div 
                key={idx} 
                className="flex-1 bg-vibebops-accent/20 rounded-t"
                style={{ height: `${value}%` }}
              />
            ))}
          </div>
        ) : (
          <div className="h-16 mt-3 flex items-center justify-center">
            <span className="text-xs text-vibebops-textMuted">No chart data available</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StatCard;
